// Resource-node regeneration (design doc 16.53, LOCKED interview pass):
// real time-based regen, tracked independently per node, continuing
// whether the player is present at that Location or not. Full restock in
// 5 real game-clock hours from 0% to 100%, linear (`poolMaxM3 / 5h` per
// hour).
//
// Deliberately keyed off raw wall-clock time (`Date.now()`), NOT the
// pausable `effectiveNow`/ClockState used elsewhere (VIP deadlines,
// hired-ship upkeep) -- those intentionally pause while logged out/docked,
// which is the opposite of "keeps regenerating whether the player is
// present or not." Nodes are only `belt`/`mining`-kind asteroid nodes
// (expeditionScreen.ts's `rebuildLocationScene`) -- a `missionMining`
// Location's dynamically-sized, mission-scoped node pool is excluded, it
// isn't a persistent world resource.
export interface NodeRegenRecord {
  key: string; // `${systemId}:${locationId}:${nodeId}` -- node ids ("ast1"/"ast2") are reused across every belt/mining Location, so the key must be composite
  poolM3: number;
  poolMaxM3: number;
  lastUpdatedAt: number; // raw epoch ms
}

const REGEN_HOURS = 5;
const MS_PER_HOUR = 60 * 60 * 1000;
const FULL_REGEN_MS = REGEN_HOURS * MS_PER_HOUR;

export function nodeRegenKey(systemId: string, locationId: string, nodeId: string): string {
  return `${systemId}:${locationId}:${nodeId}`;
}

export function findNodeRegen(records: NodeRegenRecord[], key: string): NodeRegenRecord | undefined {
  return records.find((r) => r.key === key);
}

// Looks up a node's persisted regen state and applies elapsed-time regen
// against it, creating a fresh full-pool record on first-ever visit (a
// node that's never been persisted starts full, matching the old
// always-full behavior for a node nobody has touched yet). Never mutates
// `records` itself -- callers decide whether/how to write the result back
// (see upsertNodeRegen).
export function readNodePool(records: NodeRegenRecord[], key: string, poolMaxM3: number, nowMs: number): NodeRegenRecord {
  const existing = findNodeRegen(records, key);
  if (!existing) {
    return { key, poolM3: poolMaxM3, poolMaxM3, lastUpdatedAt: nowMs };
  }
  const elapsedMs = Math.max(0, nowMs - existing.lastUpdatedAt);
  if (elapsedMs === 0) {
    return existing;
  }
  const regenerated = (elapsedMs / FULL_REGEN_MS) * existing.poolMaxM3;
  const poolM3 = Math.min(existing.poolMaxM3, existing.poolM3 + regenerated);
  return { ...existing, poolM3, lastUpdatedAt: nowMs };
}

// Writes a node's current pool back into the persisted record collection
// (insert-or-replace by key) -- called both after reading (to advance
// `lastUpdatedAt` and avoid recomputing from an ever-older timestamp) and
// after every real extraction tick (so mining is what actually persists,
// same as the live FixedObject mutation it mirrors).
export function upsertNodeRegen(records: NodeRegenRecord[], record: NodeRegenRecord): NodeRegenRecord[] {
  const idx = records.findIndex((r) => r.key === record.key);
  if (idx === -1) {
    return [...records, record];
  }
  const next = records.slice();
  next[idx] = record;
  return next;
}
